import { Box, Heading, MessageBlock } from '@deckee/deck-hand';
import React, { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { captureEvent } from '../../../../services/event-capture';
import { openWithInAppBrowser } from '../../../../services/in-app-browser';
import { useAppDispatch } from '../../../../services/store';
import {
  searchCourses,
  selectCourseSearchTerm,
  selectIsFetchingCourses,
  selectSelectedCourseCategory,
  selectViewableCourses,
  setSearchResults,
  setSelectedCourseCategory,
} from '../../../../services/store/modules/courses';
import LogoSpinner from '../../../shared/_deprecated/components/LogoSpinner/LogoSpinner';

import CourseCard, { CourseCardInterface } from '../components/CourseCard';

const handleSelectCourse = async (course: CourseCardInterface) => {
  captureEvent({
    type: 'courses: enroll selected',
    payload: course,
    sendToAmplitude: true,
  });

  const { url } = course;

  // Encode the url params as there was an issue with spaces causing url's not to open with the in-app browser
  const urlArray = url.split('?');
  const urlParams = urlArray[1].split('&');
  const encodedParams: string[] = [];
  urlParams.forEach((param) => {
    const paramArray = param.split('=');
    encodedParams.push(`${paramArray[0]}=${encodeURIComponent(paramArray[1])}`);
  });
  const safeUrl = `${urlArray[0]}?${encodedParams.join('&')}`;

  try {
    await openWithInAppBrowser({ url: safeUrl });
  } catch (err) {
    window.location.href = safeUrl;
  }
};

const CourseListPage: FC = () => {
  const courses = useSelector(selectViewableCourses);
  const courseSearchTerm = useSelector(selectCourseSearchTerm);
  const selectedCategory = useSelector(selectSelectedCourseCategory);
  const location = useLocation();
  const fetchingCourses = useSelector(selectIsFetchingCourses);
  const searchParams = new URLSearchParams(location.search);
  const category = searchParams.get('category');
  const dispatch = useAppDispatch();

  useEffect(
    () => () => {
      dispatch(setSearchResults([]));
    },
    [dispatch],
  );

  useEffect(() => {
    dispatch(setSelectedCourseCategory(category || undefined));

    const searchPromise = dispatch(
      searchCourses({
        // Added this check because the URLSearchParams returns 'null' if not found, but we need undefined
        category: category || undefined,
        searchTerm: courseSearchTerm,
      }),
    );

    return () => {
      if (searchPromise) searchPromise.abort();
    };
  }, [category, courseSearchTerm, dispatch]);

  return (
    <Box p="3">
      {courseSearchTerm && (
        <Heading textAlign="center" level={1} displayLevel={6} pb="3">
          {courses.length} course{courses.length !== 1 && 's'} found for &quot;
          {courseSearchTerm}&quot;
          {selectedCategory && ` within ${selectedCategory.name}`}
        </Heading>
      )}
      {!courseSearchTerm && selectedCategory && (
        <>
          <Heading textAlign="center" level={1} displayLevel={4} pb="3">
            {selectedCategory.name}
          </Heading>
          {!fetchingCourses && courses.length === 0 && (
            <MessageBlock mt="2" fontWeight="500" type="warning">
              No courses found within {selectedCategory.name}
            </MessageBlock>
          )}
        </>
      )}
      {fetchingCourses && <LogoSpinner />}
      <div role="list" aria-label="courses-list">
        {!fetchingCourses &&
          courses.map((course) => (
            <CourseCard
              key={course.id}
              {...course}
              role="listitem"
              onSelectCourse={handleSelectCourse}
            />
          ))}
      </div>
    </Box>
  );
};

export default CourseListPage;
